import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";

import SkillProviderAuthContext from "../../context/SkillProviderAuthContext";
import CalendarSection from "../CounsellorPortal/CalendarSection";
import DashboardBookingsTable from "../CounsellorPortal/DashboardBookingsTable";
import EditProfileDialog from "../CounsellorPortal/EditProfileDialog";
import {
  fetchSkillProviderPortalCalendarSlots,
  fetchSkillProviderPortalBookings,
  fetchSkillProviderPortalDashboard,
  fetchSkillProviderPortalDashboardBookings,
  fetchSkillProviderPortalProfile,
  fetchSkillProviderServiceBookings,
  publishSkillProviderPortalCalendarSlots,
  unpublishSkillProviderPortalCalendarSlots,
  updateSkillProviderPortalProfile,
} from "../../services/skillProviderPortalApi";
import { useToast } from "../../hook/useToast";

const DASHBOARD_SCALE = 0.75;
const EXPANDED_WIDTH = 308;
const COLLAPSED_WIDTH = 72;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

const SECTION_LABELS = {
  dashboard: "Welcome to Skill Provider Dashboard",
  calendar: "Calendar",
  bookings: "Bookings",
  payments: "Payments",
  profile: "Profile",
  "service-bookings": "Service Booking",
};

const menuItems = [
  {
    key: "dashboard",
    label: "Dashboard",
    path: "/skill-provider-portal/dashboard",
    icon: DashboardOutlinedIcon,
  },
  {
    key: "calendar",
    label: "Calendar",
    path: "/skill-provider-portal/calendar",
    icon: CalendarMonthOutlinedIcon,
  },
  {
    key: "bookings",
    label: "Bookings",
    path: "/skill-provider-portal/bookings",
    icon: TableChartOutlinedIcon,
  },
  {
    key: "payments",
    label: "Payments",
    path: "/skill-provider-portal/payments",
    icon: PaymentsOutlinedIcon,
  },
  {
    key: "profile",
    label: "Profile",
    path: "/skill-provider-portal/profile",
    icon: PersonOutlineOutlinedIcon,
  },
  {
    key: "service-bookings",
    label: "Service Booking",
    path: "/skill-provider-portal/service-bookings",
    icon: TableChartOutlinedIcon,
  },
];

const SERVICE_BOOKING_COLUMNS = [
  { id: "sr_no", label: "Sr No", align: "left", width: 80 },
  { id: "student", label: "Student", align: "left", width: 210 },
  { id: "city", label: "City", align: "left", width: 150 },
  { id: "date", label: "Date", align: "left", width: 150 },
  { id: "gmail_id", label: "Gmail id", align: "left", width: 230 },
  { id: "service_name", label: "service name", align: "left", width: 220 },
  { id: "booking_amount", label: "Booking amount", align: "left", width: 170 },
  { id: "start_date", label: "start date", align: "left", width: 160 },
  { id: "end_date", label: "end date", align: "left", width: 160 },
];

const normalizeMediaUrl = (value) => {
  const url = String(value || "").trim();
  if (!url || /^(https?:|blob:|data:)/i.test(url)) return url;

  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(url, `${origin}/`).href;
  } catch {
    return url;
  }
};

const normalizeExternalUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const getInitials = (name = "") => {
  const trimmed = String(name).trim();
  if (!trimmed) return "?";
  const [first, second] = trimmed.split(/\s+/);
  return `${first?.[0] || ""}${second?.[0] || ""}`.toUpperCase() || "?";
};

const valueOrDash = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return "--";
  return value;
};

const formatIndianRupee = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "Rs 0";
  return `Rs ${amount.toLocaleString("en-IN")}`;
};

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("en-IN") : "--";
};

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
};

const getNestedValue = (obj, paths, fallback = "") => {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, key) => acc?.[key], obj);
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return fallback;
};

const getCertificateUrl = (certificate) => {
  if (!certificate) return "";
  if (typeof certificate === "object") {
    return normalizeMediaUrl(certificate.file || certificate.url || certificate.certificate || "");
  }
  return normalizeMediaUrl(certificate);
};

const getCertificateName = (certificate, index) => {
  if (!certificate) return `Certificate ${index + 1}`;
  if (typeof certificate === "object") {
    const fileUrl = certificate.file || certificate.url || certificate.certificate || "";
    return (
      certificate.name ||
      certificate.original_name ||
      String(fileUrl).split("/").pop() ||
      `Certificate ${index + 1}`
    );
  }
  return String(certificate).split("/").pop() || `Certificate ${index + 1}`;
};

const getProfileCertificates = (profile) => {
  const certificates = Array.isArray(profile?.certificates)
    ? profile.certificates
        .map((certificate, index) => ({
          id: certificate?.certificate_id || certificate?.id || `certificate-${index}`,
          name: getCertificateName(certificate, index),
          url: getCertificateUrl(certificate),
        }))
        .filter((certificate) => certificate.url)
    : [];

  const legacyCertificateUrl = getCertificateUrl(profile?.certificate);
  if (
    legacyCertificateUrl &&
    !certificates.some((certificate) => certificate.url === legacyCertificateUrl)
  ) {
    certificates.unshift({
      id: "legacy-certificate",
      name: getCertificateName(profile.certificate, 0),
      url: legacyCertificateUrl,
    });
  }

  return certificates;
};

const RecordsStatCard = ({
  studentLabel,
  studentValue,
  earningLabel,
  earningValue,
  icon: Icon,
  variant = "navy",
}) => {
  const palettes = {
    navy: {
      background: "#F5F6FA",
      labelColor: "#687083",
      textColor: "#111827",
      iconBg: "#FFFFFF",
    },
    cream: {
      background: "#FFF7EC",
      labelColor: "#7A6A52",
      textColor: "#1F1B16",
      iconBg: "#FFFFFF",
    },
    blue: {
      background: "#EEF2F7",
      labelColor: "#475467",
      textColor: "#0F172A",
      iconBg: "#FFFFFF",
    },
  };
  const palette = palettes[variant] || palettes.navy;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        p: { xs: 2.25, md: 2.75 },
        minHeight: 168,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        background: palette.background,
        border: "1px solid rgba(15, 23, 42, 0.04)",
        boxShadow: "0 8px 22px rgba(45, 49, 95, 0.06)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: palette.labelColor }}>
          {studentLabel}
        </Typography>
        {Icon ? (
          <Box sx={{ width: 46, height: 46, borderRadius: "10px", bgcolor: palette.iconBg, color: palette.textColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        ) : null}
      </Box>

      <Box>
        <Typography sx={{ fontSize: { xs: 28, md: 32 }, fontWeight: 800, color: palette.textColor, lineHeight: 1.1 }}>
          {studentValue ?? 0}
        </Typography>
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={{ fontSize: 13, color: palette.labelColor, fontWeight: 600 }}>
            {earningLabel}
          </Typography>
          <Typography sx={{ fontSize: { xs: 18, md: 20 }, color: palette.textColor, fontWeight: 700, mt: 0.25 }}>
            {earningValue}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

RecordsStatCard.propTypes = {
  studentLabel: PropTypes.string.isRequired,
  studentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  earningLabel: PropTypes.string.isRequired,
  earningValue: PropTypes.node,
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf(["navy", "cream", "blue"]),
};

const ComingSoonSection = ({ title }) => (
  <Box>
    <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#171725", mb: 1.4 }}>
      {title}
    </Typography>
    <Paper elevation={0} sx={{ borderRadius: "8px", bgcolor: "#fff", border: "1px solid #EFEFF4", p: 3 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#171725" }}>
        {title} module
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#667085", mt: 1 }}>
        This section is ready for the next implementation step.
      </Typography>
    </Paper>
  </Box>
);

ComingSoonSection.propTypes = {
  title: PropTypes.string.isRequired,
};

const SkillProviderSidebar = ({ onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { skillProviderUser } = useContext(SkillProviderAuthContext);

  const provider = useMemo(
    () => skillProviderUser?.skillProvider || {},
    [skillProviderUser],
  );
  const userInfo = useMemo(() => skillProviderUser?.userInfo || {}, [skillProviderUser]);
  const providerName = getNestedValue(
    provider,
    ["name", "full_name", "username"],
    getNestedValue(userInfo, ["name", "full_name", "username"], "Skill Provider"),
  );
  const providerPhoto = getNestedValue(
    provider,
    ["photo", "image", "avatar", "profile_image", "profileImage"],
    getNestedValue(userInfo, ["photo", "image", "avatar", "profile_image", "profileImage"], ""),
  );

  const isActive = (item) => {
    if (location.pathname === "/skill-provider-portal" && item.key === "dashboard") {
      return true;
    }
    return location.pathname === item.path;
  };

  return (
    <Box sx={{ width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH, height: "100%", bgcolor: "#2D315F", color: "#fff", flexShrink: 0, display: "flex", flexDirection: "column", transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", px: collapsed ? 2 : 2.25, py: 3.5, borderBottom: "1px solid rgba(255,255,255,0.12)", minHeight: 88 }}>
        <Box component="img" src={collapsed ? "/assets/images/swrizeLogoMini.png" : "/assets/images/swrizeLogo.png"} alt="Swrize" sx={{ height: 28, maxWidth: collapsed ? 36 : 130, objectFit: "contain", flexShrink: 0 }} />
        {!collapsed ? (
          <IconButton aria-label="Collapse skill provider sidebar" onClick={() => setCollapsed(true)} sx={{ width: 36, height: 36, color: "#fff", border: "1px solid rgba(255,255,255,0.55)", borderRadius: "7px" }}>
            <KeyboardArrowLeftIcon />
          </IconButton>
        ) : null}
      </Box>

      {!collapsed ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", pt: 3, pb: 2, px: 2 }}>
          <Avatar src={normalizeMediaUrl(providerPhoto) || undefined} sx={{ width: 112, height: 112, bgcolor: "#FFA447", color: "#fff", fontSize: 44, fontWeight: 700, mb: 1.5 }}>
            {!providerPhoto ? getInitials(providerName) : null}
          </Avatar>
          <Typography sx={{ fontSize: 20, fontWeight: 600, color: "#fff", opacity: 0.85 }}>
            {providerName ? `@${providerName}` : "@Skill Provider"}
          </Typography>
        </Box>
      ) : (
        <IconButton aria-label="Expand skill provider sidebar" onClick={() => setCollapsed(false)} sx={{ width: 36, height: 36, color: "#fff", border: "1px solid rgba(255,255,255,0.55)", borderRadius: "7px", mx: "auto", my: 2 }}>
          <KeyboardArrowRightIcon />
        </IconButton>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: collapsed ? 2 : 1.75, pt: 2, flex: 1 }}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const selected = isActive(item);
          const button = (
            <Button
              key={item.key}
              fullWidth
              onClick={() => navigate(item.path)}
              startIcon={<Icon sx={{ fontSize: 22 }} />}
              sx={{
                justifyContent: collapsed ? "center" : "flex-start",
                height: 45,
                px: collapsed ? 0 : 1.5,
                borderRadius: "8px",
                textTransform: "none",
                fontSize: 16,
                fontWeight: 500,
                color: "#fff",
                bgcolor: selected ? "#FFA447" : "transparent",
                minWidth: collapsed ? 40 : "auto",
                "&:hover": { bgcolor: selected ? "#FFA447" : "rgba(255,255,255,0.08)" },
                "& .MuiButton-startIcon": { mr: collapsed ? 0 : 1.5 },
              }}
            >
              <Box component="span" sx={{ overflow: "hidden", opacity: collapsed ? 0 : 1, width: collapsed ? 0 : "auto", whiteSpace: "nowrap" }}>
                {item.label}
              </Box>
            </Button>
          );

          return collapsed ? (
            <Tooltip key={item.key} title={item.label} placement="right" arrow>
              <Box sx={{ display: "flex", justifyContent: "center" }}>{button}</Box>
            </Tooltip>
          ) : (
            button
          );
        })}
      </Box>

      <Box sx={{ px: collapsed ? 2 : 1.75, py: 2, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
        <Tooltip title={collapsed ? "Logout" : ""} placement="right" arrow>
          <Button fullWidth onClick={onLogout} startIcon={<LogoutOutlinedIcon sx={{ fontSize: 22 }} />} sx={{ justifyContent: collapsed ? "center" : "flex-start", height: 45, px: collapsed ? 0 : 1.5, borderRadius: "8px", textTransform: "none", fontSize: 16, fontWeight: 500, color: "#fff", bgcolor: "transparent", minWidth: collapsed ? 40 : "auto", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" }, "& .MuiButton-startIcon": { mr: collapsed ? 0 : 1.5 } }}>
            <Box component="span" sx={{ overflow: "hidden", opacity: collapsed ? 0 : 1, width: collapsed ? 0 : "auto", whiteSpace: "nowrap" }}>
              Logout
            </Box>
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

SkillProviderSidebar.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

const SkillProviderDashboardLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const { skillProviderUser, logoutSkillProvider } = useContext(SkillProviderAuthContext);
  const [profileAnchor, setProfileAnchor] = useState(null);

  const provider = useMemo(
    () => skillProviderUser?.skillProvider || {},
    [skillProviderUser],
  );
  const userInfo = useMemo(() => skillProviderUser?.userInfo || {}, [skillProviderUser]);
  const providerName = getNestedValue(
    provider,
    ["name", "full_name", "username"],
    getNestedValue(userInfo, ["name", "full_name", "username"], "Skill Provider"),
  );
  const providerEmail = getNestedValue(provider, ["email"], getNestedValue(userInfo, ["email"], ""));
  const providerPhoto = getNestedValue(provider, ["photo"], getNestedValue(userInfo, ["photo"], ""));

  const handleLogout = () => {
    setProfileAnchor(null);
    logoutSkillProvider();
    navigate("/skill-provider-portal/login");
  };

  return (
    <Box sx={{ width: "100vw", height: "100vh", overflow: "hidden", bgcolor: "#FAFAFC" }}>
      <Box sx={{ width: `calc(100vw / ${DASHBOARD_SCALE})`, height: `calc(100vh / ${DASHBOARD_SCALE})`, transform: `scale(${DASHBOARD_SCALE})`, transformOrigin: "top left", display: "flex", bgcolor: "#FAFAFC", fontFamily: '"Inter", "Poppins", sans-serif' }}>
        <SkillProviderSidebar onLogout={handleLogout} />
        <Box sx={{ flex: 1, minWidth: 0, height: "100%", overflow: "auto", px: 3, py: 3.25, bgcolor: "#FAFAFC" }}>
          <Box sx={{ minHeight: 58, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, borderBottom: "1px solid #EFEFF4", mb: 3, pb: 1.5 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#171725" }}>{title}</Typography>
            <Box component="button" type="button" onClick={(event) => setProfileAnchor(event.currentTarget)} sx={{ display: "flex", alignItems: "center", gap: 1.5, border: "1px solid #EFEFF4", bgcolor: "#fff", borderRadius: "10px", px: 1.2, py: 0.8, cursor: "pointer", textAlign: "left", font: "inherit", "&:hover": { bgcolor: "#F8F9FC" } }}>
              <Avatar src={normalizeMediaUrl(providerPhoto) || undefined} sx={{ width: 38, height: 38, bgcolor: "#2D315F", fontSize: 15, fontWeight: 700 }}>
                {!providerPhoto ? getInitials(providerName) : null}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#171725" }}>
                  {providerName}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#92929D" }}>
                  Skill Provider
                </Typography>
              </Box>
              <KeyboardArrowDownIcon sx={{ color: "#111827", fontSize: 20 }} />
            </Box>

            <Menu anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={() => setProfileAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} PaperProps={{ sx: { mt: 1, minWidth: 240, borderRadius: "10px", border: "1px solid #EFEFF4", boxShadow: "0 18px 48px rgba(45, 49, 95, 0.16)" } }}>
              <Box sx={{ px: 2, py: 1.4 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#171725" }}>{providerName}</Typography>
                <Typography sx={{ fontSize: 12, color: "#92929D", mt: 0.3 }}>{providerEmail}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ minHeight: 42, gap: 1, color: "#B42318", fontSize: 14, fontWeight: 600 }}>
                <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>

          {children}
        </Box>
      </Box>
    </Box>
  );
};

SkillProviderDashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

const DashboardSection = () => {
  const { skillProviderUser } = useContext(SkillProviderAuthContext);
  const { showToast } = useToast();
  const initialDashboard = skillProviderUser?.dashboard || {};

  const [dashboard, setDashboard] = useState({
    total_students: initialDashboard.total_students || 0,
    students_this_month: initialDashboard.students_this_month || 0,
    students_today: initialDashboard.students_today || 0,
    total_earnings: initialDashboard.total_earnings || 0,
    earnings_this_month: initialDashboard.earnings_this_month || 0,
    earnings_today: initialDashboard.earnings_today || 0,
  });
  const [loading, setLoading] = useState(false);
  const timezone = useMemo(detectTimezone, []);

  useEffect(() => {
    setLoading(true);
    fetchSkillProviderPortalDashboard({ timezone })
      .then((response) => {
        const payload = response?.data?.data || {};
        setDashboard((prev) => ({ ...prev, ...payload }));
      })
      .catch((error) => {
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load dashboard. Please try again.";
        showToast(apiMessage, "error");
      })
      .finally(() => setLoading(false));
  }, [showToast, timezone]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#171725" }}>
          Records
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} sx={{ color: "#2D315F" }} />
            <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>
              Refreshing...
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
        <RecordsStatCard studentLabel="Total Students" studentValue={dashboard.total_students} earningLabel="Total Earning" earningValue={formatIndianRupee(dashboard.total_earnings)} icon={SpaOutlinedIcon} variant="navy" />
        <RecordsStatCard studentLabel="Students this Month" studentValue={dashboard.students_this_month} earningLabel="Earning this month" earningValue={formatIndianRupee(dashboard.earnings_this_month)} icon={PersonAddAlt1OutlinedIcon} variant="cream" />
        <RecordsStatCard studentLabel="Students Today" studentValue={dashboard.students_today} earningLabel="Earning Today" earningValue={formatIndianRupee(dashboard.earnings_today)} icon={TableChartOutlinedIcon} variant="blue" />
      </Box>

      <DashboardBookingsTable
        fetchBookings={fetchSkillProviderPortalDashboardBookings}
        title="Bookings for Counseling"
      />
    </Box>
  );
};

const DetailHeading = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 1.5 }}>
    <Typography sx={{ color: "#171725", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
      {children}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#E4E7EC" }} />
  </Box>
);

DetailHeading.propTypes = {
  children: PropTypes.node.isRequired,
};

const ProfileInfoField = ({ label, value, href, multiline = false }) => {
  const displayValue = valueOrDash(value);
  const isLinked = Boolean(href && displayValue !== "--");

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "155px minmax(0, 1fr)" }, alignItems: multiline ? "flex-start" : "center", columnGap: 2, rowGap: 0.6, minHeight: multiline ? "auto" : 28 }}>
      <Typography sx={{ fontSize: 13, color: "#667085", lineHeight: 1.6 }}>
        {label} :
      </Typography>
      <Typography component={isLinked ? "a" : "span"} href={isLinked ? href : undefined} target={isLinked ? "_blank" : undefined} rel={isLinked ? "noopener noreferrer" : undefined} sx={{ color: isLinked ? "#1F4FB6" : "#111827", fontSize: 13.5, fontWeight: 500, lineHeight: 1.55, textDecoration: "none", wordBreak: "break-word", whiteSpace: multiline ? "pre-line" : "normal", "&:hover": isLinked ? { textDecoration: "underline" } : undefined }}>
        {displayValue}
      </Typography>
    </Box>
  );
};

ProfileInfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  href: PropTypes.string,
  multiline: PropTypes.bool,
};

const ProfileSection = () => {
  const { skillProviderUser, updateSkillProviderProfile } = useContext(SkillProviderAuthContext);
  const { showToast } = useToast();
  const [profile, setProfile] = useState(skillProviderUser?.skillProvider || {});
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchSkillProviderPortalProfile()
      .then((response) => setProfile(response?.data?.data || {}))
      .catch((error) => {
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load profile. Please try again.";
        showToast(apiMessage, "error");
      })
      .finally(() => setLoading(false));
  }, [showToast]);

  const displayName = profile.name || profile.full_name || "Skill Provider";
  const email = profile.email || skillProviderUser?.userInfo?.email || "";
  const photoUrl = normalizeMediaUrl(profile.photo);
  const linkedinUrl = normalizeExternalUrl(profile.linkedin);
  const certificates = useMemo(() => getProfileCertificates(profile), [profile]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#171725" }}>
        Profile
      </Typography>

      <Paper elevation={0} sx={{ borderRadius: "8px", border: "1px solid #DDE1EA", bgcolor: "#fff", position: "relative", overflow: "hidden" }}>
        <Box sx={{ height: { xs: 132, md: 152 }, backgroundImage: "url('/assets/newImages/Backdrop.png')", backgroundSize: "cover", backgroundPosition: "center" }} />
        {loading ? (
          <Box sx={{ position: "absolute", top: 16, right: 18, display: "flex", alignItems: "center", gap: 1, color: "#667085" }}>
            <CircularProgress size={18} sx={{ color: "#2D315F" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Loading</Typography>
          </Box>
        ) : null}
        <Box sx={{ position: "relative", minHeight: { xs: 220, md: 164 }, px: { xs: 2.25, md: 3.75 }, pt: { xs: 7.8, md: 7.3 }, pb: 2.4 }}>
          <Avatar src={photoUrl || undefined} sx={{ position: "absolute", top: { xs: -54, md: -68 }, left: { xs: 22, md: 36 }, width: { xs: 108, md: 124 }, height: { xs: 108, md: 124 }, borderRadius: "10px", border: "4px solid #fff", bgcolor: "#2D315F", color: "#fff", fontSize: 34, fontWeight: 800, boxShadow: "0 18px 40px rgba(45, 49, 95, 0.18)" }}>
            {!photoUrl ? getInitials(displayName) : null}
          </Avatar>
          <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ minWidth: 0, maxWidth: 720 }}>
              <Typography sx={{ fontSize: { xs: 26, md: 30 }, fontWeight: 500, color: "#111827", lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis" }}>
                @{displayName}
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 0.75, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis" }}>
                {email || "--"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.8, lineHeight: 1.55, maxWidth: 780, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {profile.description || "No description available."}
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => setEditDialogOpen(true)} sx={{ alignSelf: { xs: "flex-start", md: "center" }, textTransform: "none", bgcolor: "#FFA447", color: "#fff", fontWeight: 700, fontSize: 15, borderRadius: "999px", px: 2.5, py: 0.9, boxShadow: "none", flexShrink: 0, whiteSpace: "nowrap", "&:hover": { bgcolor: "#F5912E", boxShadow: "none" } }}>
              Edit / Update
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.4 }, borderRadius: "8px", border: "none", bgcolor: "#EDEEF1" }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.4 }, borderRadius: "8px", border: "1px solid #DDE1EA", bgcolor: "#fff" }}>
          <DetailHeading>Personal Information</DetailHeading>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.15, mb: 3.25 }}>
            <ProfileInfoField label="Full Name" value={profile.full_name || profile.name} />
            <ProfileInfoField label="Email" value={email} />
            <ProfileInfoField label="Phone No." value={profile.phone_number} />
            <ProfileInfoField label="Year of experience" value={profile.years_of_experience} />
            <ProfileInfoField label="Skill" value={profile.skill || profile.skills} />
            <ProfileInfoField label="City" value={profile.city} />
          </Box>

          <DetailHeading>Contact Information</DetailHeading>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 3.2 }}>
            <ProfileInfoField label="Linked in" value={profile.linkedin} href={linkedinUrl} />
            <ProfileInfoField label="Description" value={profile.description || "No description available."} multiline />
          </Box>

          <DetailHeading>Certificates</DetailHeading>
          {certificates.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.3 }}>
              {certificates.map((certificate, index) => (
                <Paper key={certificate.id || certificate.url} component="a" href={certificate.url} target="_blank" rel="noopener noreferrer" elevation={0} sx={{ p: 1.5, borderRadius: "8px", border: "1px solid #EFEFF4", bgcolor: "#fff", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, "&:hover": { borderColor: "#C9D7FE", bgcolor: "#FBFCFF" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 1.25 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: "#FFF6E5", color: "#B54708", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <WorkspacePremiumOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#171725", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {certificate.name || `Certificate ${index + 1}`}
                    </Typography>
                  </Box>
                  <LaunchOutlinedIcon sx={{ fontSize: 18, color: "#1F4FB6", flexShrink: 0 }} />
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13.5, color: "#98A2B3" }}>
              No certificates available.
            </Typography>
          )}
        </Paper>
      </Paper>

      <EditProfileDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        initialValues={profile}
        includeSkill
        updateProfile={updateSkillProviderPortalProfile}
        fallbackDisplayName="Skill Provider Name"
        onSuccess={(updatedProfile) => {
          if (updatedProfile && typeof updatedProfile === "object") {
            setProfile(updatedProfile);
            updateSkillProviderProfile?.(updatedProfile);
          }
          showToast("Profile updated successfully.", "success");
        }}
      />
    </Box>
  );
};

const ServiceBookingsTable = ({ compact = false }) => {
  const { showToast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: compact ? 10 : 20, total_records: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(compact ? 10 : 20);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => {
      setPage(1);
      setAppliedSearch(search.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    fetchSkillProviderServiceBookings({ page, pageSize, search: appliedSearch })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        const payload = response?.data?.data || {};
        setBookings(Array.isArray(payload.bookings) ? payload.bookings : []);
        setPagination(payload.pagination || { page, page_size: pageSize, total_records: 0, total_pages: 0 });
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load service bookings. Please try again.";
        showToast(apiMessage, "error");
        setBookings([]);
        setPagination({ page, page_size: pageSize, total_records: 0, total_pages: 0 });
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [page, pageSize, appliedSearch, showToast]);

  const totalRecords = pagination.total_records || 0;

  return (
    <Box>
      {!compact ? (
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#171725", mb: 2 }}>
          Service Booking
        </Typography>
      ) : null}
      <Paper elevation={0} sx={{ borderRadius: "8px", bgcolor: "#fff", border: "1px solid #EFEFF4", overflow: "hidden" }}>
        <Box sx={{ minHeight: 58, px: 1.5, py: 1.2, display: "flex", alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", md: "row" }, gap: 2, borderBottom: "1px solid #EFEFF4" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#171725" }}>
            Service Booking
          </Typography>
          <TextField
            placeholder="Search student or Gmail id..."
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ width: { xs: "100%", sm: 300 }, "& .MuiOutlinedInput-root": { height: 38, borderRadius: "7px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#E4E6EF" } } }}
            InputProps={{ endAdornment: <InputAdornment position="end">{loading ? <CircularProgress size={16} /> : <SearchOutlinedIcon sx={{ fontSize: 19, color: "#2D315F" }} />}</InputAdornment> }}
          />
        </Box>

        <TableContainer sx={{ overflowX: "auto", "&::-webkit-scrollbar": { height: 8 }, "&::-webkit-scrollbar-track": { bgcolor: "#F2F3F7" }, "&::-webkit-scrollbar-thumb": { bgcolor: "#C6CAD8", borderRadius: 20 } }}>
          <Table sx={{ minWidth: 1480, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#FAFBFD" }}>
                {SERVICE_BOOKING_COLUMNS.map((column) => (
                  <TableCell key={column.id} align={column.align} sx={{ width: column.width, maxWidth: column.width, height: 45, py: 0, pl: column.align === "left" ? 2.5 : 1.5, pr: 1.5, fontSize: 12, fontWeight: 600, color: "#6B7280", borderBottom: "1px solid #EFEFF4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={SERVICE_BOOKING_COLUMNS.length} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={SERVICE_BOOKING_COLUMNS.length} align="center" sx={{ py: 8, color: "#92929D", fontSize: 14 }}>
                    {appliedSearch ? "No service bookings match your search" : "No service bookings found"}
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking, index) => (
                  <TableRow key={booking.booking_id || `${booking.gmail_id}-${index}`} sx={{ height: 64, "& td": { borderBottom: "1px solid #EFEFF4" }, "&:hover": { bgcolor: "#FCFCFE" } }}>
                    <TableCell sx={{ pl: 3, fontSize: 13, color: "#111827" }}>{booking.sr_no ?? index + 1}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#171725", fontWeight: 600 }}>{booking.student || "--"}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#4A4F63" }}>{booking.city || "--"}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#4A4F63" }}>{booking.date || "--"}</TableCell>
                    <TableCell title={booking.gmail_id || ""} sx={{ fontSize: 13, color: "#4A4F63", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{booking.gmail_id || "--"}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#171725" }}>{booking.service_name || "--"}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#171725" }}>{booking.booking_amount ? formatIndianRupee(booking.booking_amount) : "--"}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#4A4F63" }}>{booking.start_date || "--"}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#4A4F63" }}>{booking.end_date || "--"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, px: 2, py: 1.5, borderTop: "1px solid #EFEFF4" }}>
          <Typography sx={{ fontSize: 13, color: "#475467" }}>
            Total - {formatNumber(totalRecords)}
          </Typography>
          {pagination.total_pages > 1 ? (
            <Pagination count={pagination.total_pages} page={pagination.page || page} onChange={(_event, value) => setPage(value)} shape="rounded" siblingCount={1} boundaryCount={1} sx={{ "& .MuiPaginationItem-root.Mui-selected": { bgcolor: "#2D315F", color: "#fff", "&:hover": { bgcolor: "#1F2347" } } }} />
          ) : (
            <Box />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

ServiceBookingsTable.propTypes = {
  compact: PropTypes.bool,
};

const SkillProviderDashboardPage = () => {
  const { section = "dashboard" } = useParams();
  const { skillProviderUser } = useContext(SkillProviderAuthContext);
  const profileTitleName =
    skillProviderUser?.skillProvider?.name ||
    skillProviderUser?.skillProvider?.full_name ||
    skillProviderUser?.userInfo?.name ||
    "Skill Provider";

  if (section === "portal") {
    return <Navigate to="/skill-provider-portal/dashboard" replace />;
  }

  if (!SECTION_LABELS[section]) {
    return <Navigate to="/skill-provider-portal/dashboard" replace />;
  }

  return (
    <SkillProviderDashboardLayout
      title={section === "profile" ? `Welcome ${profileTitleName}` : SECTION_LABELS[section]}
    >
      {section === "dashboard" ? (
        <DashboardSection />
      ) : section === "calendar" ? (
        <CalendarSection
          fetchCalendarSlots={fetchSkillProviderPortalCalendarSlots}
          publishCalendarSlots={publishSkillProviderPortalCalendarSlots}
          unpublishCalendarSlots={unpublishSkillProviderPortalCalendarSlots}
        />
      ) : section === "bookings" ? (
        <DashboardBookingsTable
          fetchBookings={fetchSkillProviderPortalBookings}
          title="Bookings for Counseling"
          showBookingMonth
        />
      ) : section === "profile" ? (
        <ProfileSection />
      ) : section === "service-bookings" ? (
        <ServiceBookingsTable />
      ) : (
        <ComingSoonSection title={SECTION_LABELS[section]} />
      )}
    </SkillProviderDashboardLayout>
  );
};

export default SkillProviderDashboardPage;
